import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, MenuItem, Pagination, Paper, Tab, Tabs, TextField, Typography
} from '@mui/material';
import {
  CalculateOutlined, CalendarMonthOutlined, PaidOutlined, SearchOutlined,
  TrendingDownOutlined, ViewListOutlined
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../services/api';
import { useLogowanie } from '../context/KontekstLogowania';

const card={border:'1px solid #e3eaf3',boxShadow:'0 8px 26px rgba(18,46,84,.055)',borderRadius:3,bgcolor:'#fff'};
const money=v=>Number(v||0).toLocaleString('pl-PL',{minimumFractionDigits:2,maximumFractionDigits:2})+' zł';

const metodaLabel=m=>({LINIOWA:'Liniowa',DEGRESYWNA:'Degresywna',JEDNORAZOWA:'Jednorazowa'}[m]||m||'—');
const czyDataHistoryczna=iso=>{
  if(!iso) return false;
  const d=new Date(`${iso}T00:00:00`);
  if(Number.isNaN(d.getTime())) return false;
  const dzis=new Date();
  return d < new Date(dzis.getFullYear(),dzis.getMonth(),1);
};
const isoData=v=>v?String(v).slice(0,10):'';
const dataPL=v=>{
  const iso=isoData(v);
  if(!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso||'—';
  const [r,m,d]=iso.split('-');
  return `${d}.${m}.${r}`;
};
const pierwszyDzienNastepnegoMiesiaca=v=>{
  const iso=isoData(v);
  if(!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [r,m]=iso.split('-').map(Number);
  const d=new Date(Date.UTC(r,m,1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-01`;
};
const poprzedniMiesiacDlaStartu=v=>{
  const iso=isoData(v);
  if(!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [r,m]=iso.split('-').map(Number);
  const d=new Date(Date.UTC(r,m-2,1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-01`;
};
const najpozniejszaDataSrodka=s=>{
  if(!s) return '';
  const daty=[isoData(s.data_zakupu),isoData(s.data_przyjecia)].filter(Boolean).sort();
  return daty.length?daty[daty.length-1]:'';
};

const METODY=[
  {kod:'LINIOWA',nazwa:'Liniowa',opis:'Stały odpis w kolejnych miesiącach.',wzor:'Odpis roczny = wartość początkowa × stawka; miesięczny = odpis roczny / 12'},
  {kod:'DEGRESYWNA',nazwa:'Degresywna',opis:'Wyższe odpisy na początku; później system przechodzi na liniową, gdy jest korzystniejsza.',wzor:'Odpis roczny = wartość netto × stawka × współczynnik'},
  {kod:'JEDNORAZOWA',nazwa:'Jednorazowa',opis:'Cała wartość jest odpisywana jednorazowo. Zastosowanie wymaga spełnienia warunków podatkowych.',wzor:'Odpis = wartość początkowa'}
];

export default function Amortyzacja(){
  const { maUprawnienie }=useLogowanie();
  const [params,setParams]=useSearchParams();
  const [tab,setTab]=useState(params.get('srodek') || params.get('widok')==='kalkulator' ? 'kalkulator' : 'ewidencja');
  const [srodki,setSrodki]=useState([]);
  const [szukaj,setSzukaj]=useState('');
  const [status,setStatus]=useState('WSZYSTKIE');
  const [strona,setStrona]=useState(1);
  const [wybranyId,setWybranyId]=useState(params.get('srodek')||'');
  const [tryb,setTryb]=useState(params.get('srodek')?'SRODEK':'SRODEK');
  const [wynik,setWynik]=useState(null);
  const [blad,setBlad]=useState('');
  const [info,setInfo]=useState('');
  const [potwierdz,setPotwierdz]=useState(false);
  const [stronaH,setStronaH]=useState(1);
  const [form,setForm]=useState({
    metoda_kod:'LINIOWA',wartosc_poczatkowa:'',stawka_zastosowana_proc:'30',wspolczynnik_degresywny:'2',
    data_wprowadzenia:'',data_rozpoczecia_ui:'',uwagi:''
  });
  const NA_STRONIE=10,H_NA_STRONIE=12;

  const load=async()=>{
    try{setSrodki(await apiFetch('/amortyzacja/srodki'));}catch(e){setBlad(e.message);}
  };
  useEffect(()=>{load();},[]);

  useEffect(()=>{
    if(!wybranyId || tryb!=='SRODEK') return;
    apiFetch(`/amortyzacja/srodki/${wybranyId}`).then(r=>{
      const s=r.srodek,p=r.plan;
      const datyBazowe=[isoData(s.data_zakupu),isoData(s.data_przyjecia)].filter(Boolean).sort();
      const dataBazowa=datyBazowe.length?datyBazowe[datyBazowe.length-1]:'';
      const sugerowanyStart=p?.data_rozpoczecia
        ? isoData(p.data_rozpoczecia)
        : pierwszyDzienNastepnegoMiesiaca(dataBazowa);

      setForm({
        metoda_kod:p?.metoda_kod||'LINIOWA',
        wartosc_poczatkowa:String(p?.wartosc_poczatkowa??''),
        stawka_zastosowana_proc:String(p?.stawka_zastosowana_proc??s.stawka_sugerowana_kst??30),
        wspolczynnik_degresywny:String(p?.wspolczynnik_degresywny??2),
        data_wprowadzenia:p?.data_wprowadzenia
          ? isoData(p.data_wprowadzenia)
          : dataBazowa,
        data_rozpoczecia_ui:sugerowanyStart,
        uwagi:p?.uwagi||''
      });
      if(p && r.harmonogram?.length){
        setWynik({data_rozpoczecia:p.data_rozpoczecia,data_zakonczenia:r.harmonogram[r.harmonogram.length-1]?.data_odpisu,liczba_miesiecy:r.harmonogram.length,harmonogram:r.harmonogram});
      }else setWynik(null);
    }).catch(e=>setBlad(e.message));
  },[wybranyId,tryb]);

  const lista=useMemo(()=>{
    const q=szukaj.trim().toLowerCase();
    let a=[...srodki];
    if(status==='AKTYWNE') a=a.filter(x=>x.id_planu);
    if(status==='BRAK') a=a.filter(x=>!x.id_planu);
    if(q) a=a.filter(x=>[x.numer_unikatowy,x.numer_inw,x.nazwa_indeksu,x.producent,x.model,x.kategoria].some(v=>String(v||'').toLowerCase().includes(q)));
    const pages=Math.max(1,Math.ceil(a.length/NA_STRONIE)),page=Math.min(strona,pages);
    return {dane:a.slice((page-1)*NA_STRONIE,page*NA_STRONIE),liczba:a.length,pages,page};
  },[srodki,szukaj,status,strona]);

  const wybrany=srodki.find(x=>String(x.id_srodka)===String(wybranyId));
  const stawkaSug=wybrany?.stawka_sugerowana_kst ?? 30;
  const minimalnaDataWprowadzenia=tryb==='SRODEK'?najpozniejszaDataSrodka(wybrany):'';
  const dataZakupuWybranego=tryb==='SRODEK'?isoData(wybrany?.data_zakupu):'';
  const sugerowanyPierwszyOdpis=tryb==='SRODEK'
    ? pierwszyDzienNastepnegoMiesiaca(dataZakupuWybranego || minimalnaDataWprowadzenia)
    : form.data_rozpoczecia_ui;

  const policz=async()=>{
    try{
      setBlad('');setInfo('');setStronaH(1);
      const r=await apiFetch('/amortyzacja/kalkulator',{method:'POST',body:JSON.stringify({
        ...form,wartosc_poczatkowa:Number(form.wartosc_poczatkowa),stawka_zastosowana_proc:Number(form.stawka_zastosowana_proc),
        wspolczynnik_degresywny:Number(form.wspolczynnik_degresywny||2)
      })});
      setWynik(r);
    }catch(e){setWynik(null);setBlad(e.message);}
  };

  const zapisz=async()=>{
    try{
      setBlad('');setInfo('');
      if(!wybranyId) return setBlad('Wybierz środek trwały, dla którego chcesz zapisać amortyzację.');

      const zapisanyId=wybranyId;
      await apiFetch(`/amortyzacja/srodki/${zapisanyId}`,{method:'PUT',body:JSON.stringify({
        ...form,wartosc_poczatkowa:Number(form.wartosc_poczatkowa),stawka_zastosowana_proc:Number(form.stawka_zastosowana_proc),
        wspolczynnik_degresywny:Number(form.wspolczynnik_degresywny||2)
      })});

      setPotwierdz(false);
      setWynik(null);
      setStronaH(1);
      setWybranyId('');
      setForm({
        metoda_kod:'LINIOWA',
        wartosc_poczatkowa:'',
        stawka_zastosowana_proc:'30',
        wspolczynnik_degresywny:'2',
        data_wprowadzenia:'',
        data_rozpoczecia_ui:'',
        uwagi:''
      });

      await load();
      setStatus('WSZYSTKIE');
      setStrona(1);
      setTab('ewidencja');
      setParams({});
      setInfo('Plan amortyzacji został zapisany. Środek jest widoczny w ewidencji amortyzacji.');
    }catch(e){
      setPotwierdz(false);
      setBlad(e.message);
    }
  };

  const harmonogram=wynik?.harmonogram||[];
  const hPages=Math.max(1,Math.ceil(harmonogram.length/H_NA_STRONIE)),hPage=Math.min(stronaH,hPages);
  const hDane=harmonogram.slice((hPage-1)*H_NA_STRONIE,hPage*H_NA_STRONIE);

  return <Box sx={{minWidth:0,maxWidth:'100%',overflow:'hidden'}}> 
    <Box sx={{mb:2}}>
      <Typography variant="h4" fontWeight={900}>Amortyzacja</Typography>
      <Typography color="text.secondary">Ewidencja, kalkulator i harmonogram odpisów środków trwałych.</Typography>
    </Box>

    {blad&&<Alert severity="error" sx={{mb:2}}>{blad}</Alert>}
    {info&&<Alert severity="success" sx={{mb:2}}>{info}</Alert>}

    <Paper sx={{...card,mb:2,px:2}}>
      <Tabs value={tab} onChange={(_,v)=>setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
        <Tab value="ewidencja" icon={<ViewListOutlined/>} iconPosition="start" label="Ewidencja amortyzacji"/>
        <Tab value="kalkulator" icon={<CalculateOutlined/>} iconPosition="start" label="Kalkulator amortyzacji"/>
      </Tabs>
    </Paper>

    {tab==='ewidencja'&&<>
      <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',sm:'repeat(2,minmax(0,1fr))',lg:'repeat(4,minmax(0,1fr))'},gap:2,mb:2}}>
        {[
          ['Środki z planem',srodki.filter(x=>x.id_planu).length,<PaidOutlined/>],
          ['Bez planu',srodki.filter(x=>!x.id_planu).length,<CalendarMonthOutlined/>],
          ['Wartość początkowa',money(srodki.reduce((a,x)=>a+Number(x.wartosc_poczatkowa||0),0)),<CalculateOutlined/>],
          ['Bieżąca wartość netto',money(srodki.reduce((a,x)=>a+Number(x.wartosc_netto_biezaca||0),0)),<TrendingDownOutlined/>]
        ].map(([l,v,i])=><Paper key={l} sx={{...card,p:2}}><Box sx={{color:'#2364d8',mb:1}}>{i}</Box><Typography variant="caption" color="text.secondary">{l}</Typography><Typography variant="h6" fontWeight={900}>{v}</Typography></Paper>)}
      </Box>

      <Paper sx={{...card,p:1.5,mb:2}}><Box sx={{display:'flex',gap:1,justifyContent:'space-between',flexWrap:'wrap'}}>
        <TextField size="small" value={szukaj} placeholder="Szukaj po numerze, nazwie, modelu lub producencie..." onChange={e=>{setSzukaj(e.target.value);setStrona(1);}} sx={{minWidth:{xs:'100%',sm:390}}} InputProps={{startAdornment:<InputAdornment position="start"><SearchOutlined fontSize="small"/></InputAdornment>}}/>
        <Box sx={{display:'flex',gap:.7,flexWrap:'wrap'}}>{[['WSZYSTKIE','Wszystkie'],['AKTYWNE','Z planem'],['BRAK','Bez planu']].map(([v,l])=><Button key={v} size="small" variant={status===v?'contained':'text'} onClick={()=>{setStatus(v);setStrona(1);}}>{l}</Button>)}</Box>
      </Box></Paper>

      <Paper sx={{...card,p:{xs:1.2,sm:2},minWidth:0}}>
        <Box sx={{display:{xs:'none',lg:'block'}}}>
          <Box sx={{display:'grid',gridTemplateColumns:'80px minmax(170px,1.5fr) .9fr .7fr 1fr 1fr 92px',gap:1,p:1,bgcolor:'#f8fafc',borderRadius:1.5,fontWeight:850,fontSize:12}}>
            <span>Nr</span><span>Środek</span><span>Metoda</span><span>Stawka</span><span>Wartość początkowa</span><span>Wartość netto</span><span>Akcja</span>
          </Box>
          {lista.dane.length===0?<Typography sx={{py:4,textAlign:'center'}} color="text.secondary">Brak wyników.</Typography>:lista.dane.map(x=><Box key={x.id_srodka} sx={{display:'grid',gridTemplateColumns:'80px minmax(170px,1.5fr) .9fr .7fr 1fr 1fr 92px',gap:1,p:1.15,borderBottom:'1px solid #edf1f6',alignItems:'center'}}>
            <Typography variant="body2" fontWeight={900}>{x.numer_unikatowy||x.numer_inw||x.id_srodka}</Typography>
            <Box sx={{minWidth:0}}><Typography variant="body2" fontWeight={850} noWrap>{x.nazwa_indeksu||x.kategoria}</Typography><Typography variant="caption" color="text.secondary" sx={{display:'block'}} noWrap>{[x.producent,x.model].filter(Boolean).join(' · ')||'—'}</Typography></Box>
            <Chip size="small" label={x.id_planu?metodaLabel(x.metoda_kod):'Brak planu'} sx={{justifySelf:'start'}}/>
            <Typography variant="body2">{x.id_planu?`${Number(x.stawka_zastosowana_proc||0)}%`:'—'}</Typography>
            <Typography variant="body2">{x.id_planu?money(x.wartosc_poczatkowa):'—'}</Typography>
            <Typography variant="body2" fontWeight={800}>{x.id_planu?money(x.wartosc_netto_biezaca):'—'}</Typography>
            <Button size="small" variant="outlined" onClick={()=>{setWybranyId(String(x.id_srodka));setTryb('SRODEK');setTab('kalkulator');setParams({srodek:String(x.id_srodka)});}}>Otwórz</Button>
          </Box>)}
        </Box>
        <Box sx={{display:{xs:'grid',lg:'none'},gap:1}}>
          {lista.dane.length===0?<Typography sx={{py:4,textAlign:'center'}} color="text.secondary">Brak wyników.</Typography>:lista.dane.map(x=><Paper key={x.id_srodka} variant="outlined" sx={{p:1.5,borderRadius:2,borderColor:'#e3eaf3'}}>
            <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:1}}><Box sx={{minWidth:0}}><Typography variant="caption" color="text.secondary">Nr {x.numer_unikatowy||x.numer_inw||x.id_srodka}</Typography><Typography fontWeight={900}>{x.nazwa_indeksu||x.kategoria}</Typography><Typography variant="body2" color="text.secondary">{[x.producent,x.model].filter(Boolean).join(' · ')||'—'}</Typography></Box><Chip size="small" label={x.id_planu?metodaLabel(x.metoda_kod):'Brak planu'}/></Box>
            <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr 1fr',sm:'repeat(3,1fr)'},gap:1,mt:1.4}}>{[['Stawka',x.id_planu?`${Number(x.stawka_zastosowana_proc||0)}%`:'—'],['Wartość początkowa',x.id_planu?money(x.wartosc_poczatkowa):'—'],['Wartość netto',x.id_planu?money(x.wartosc_netto_biezaca):'—']].map(([l,v])=><Box key={l} sx={{p:1,bgcolor:'#f8fafc',borderRadius:1.5}}><Typography variant="caption" color="text.secondary">{l}</Typography><Typography variant="body2" fontWeight={850}>{v}</Typography></Box>)}</Box>
            <Button fullWidth size="small" variant="outlined" sx={{mt:1.4}} onClick={()=>{setWybranyId(String(x.id_srodka));setTryb('SRODEK');setTab('kalkulator');setParams({srodek:String(x.id_srodka)});}}>Otwórz amortyzację</Button>
          </Paper>)}
        </Box>
        {lista.liczba>0&&<Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',pt:2,gap:1.5,flexWrap:'wrap'}}><Typography variant="caption" color="text.secondary">Wyniki {((lista.page-1)*NA_STRONIE)+1}–{Math.min(lista.page*NA_STRONIE,lista.liczba)} z {lista.liczba}</Typography><Pagination size="small" count={lista.pages} page={lista.page} onChange={(_,p)=>setStrona(p)} color="primary"/></Box>}
      </Paper>
    </>}

    {tab==='kalkulator'&&<Box sx={{display:'grid',gap:2,minWidth:0}}>
      <Box sx={{
        display:'grid',
        gridTemplateColumns:{xs:'minmax(0,1fr)',md:'repeat(2,minmax(0,1fr))',xl:'1.05fr .9fr 1.15fr'},
        gap:2,
        alignItems:'start',
        minWidth:0
      }}>
        <Paper sx={{...card,p:2,height:'100%'}}>
          <Typography fontWeight={900} sx={{mb:1.5}}>Źródło danych</Typography>
          <Tabs value={tryb} onChange={(_,v)=>{setTryb(v);setWynik(null);}} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{mb:2}}><Tab value="SRODEK" label="Środek z ewidencji"/><Tab value="SYMULACJA" label="Własna symulacja"/></Tabs>
          {tryb==='SRODEK'&&<TextField fullWidth select label="Środek trwały" value={wybranyId} onChange={e=>{setWybranyId(e.target.value);setParams(e.target.value?{srodek:e.target.value}:{});}}>
            <MenuItem value="">Wybierz...</MenuItem>{srodki.map(x=><MenuItem key={x.id_srodka} value={x.id_srodka}>{x.numer_unikatowy||x.numer_inw||x.id_srodka} · {x.nazwa_indeksu||x.kategoria} {x.model||''}</MenuItem>)}
          </TextField>}
          {tryb==='SRODEK'&&wybrany&&<Alert severity="info" sx={{mt:1.5}}>KŚT {wybrany.symbol_kst||'491'} · sugerowana stawka podstawowa: <b>{stawkaSug}%</b>. Możesz zastosować inną stawkę, ale system pokaże różnicę.</Alert>}
          {tryb==='SYMULACJA'&&<Alert severity="info">Symulacja niczego nie zapisuje. Możesz dowolnie przeliczać warianty.</Alert>}
        </Paper>

        <Paper sx={{...card,p:2,height:'100%'}}>
          <Typography fontWeight={900} sx={{mb:1}}>Metoda amortyzacji</Typography>
          <TextField
            fullWidth
            select
            label="Wybierz metodę amortyzacji"
            value={form.metoda_kod}
            onChange={e=>setForm(f=>({...f,metoda_kod:e.target.value}))}
          >
            {METODY.map(m=><MenuItem key={m.kod} value={m.kod}>{m.nazwa}</MenuItem>)}
          </TextField>
          {form.metoda_kod&&<Box sx={{mt:1.5,p:1.4,bgcolor:'#f8fafc',border:'1px solid #e3eaf3',borderRadius:2}}>
            <Typography variant="body2" fontWeight={850}>{METODY.find(m=>m.kod===form.metoda_kod)?.nazwa}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mt:.35}}>{METODY.find(m=>m.kod===form.metoda_kod)?.opis}</Typography>
            <Typography variant="caption" sx={{display:'block',mt:.65,fontWeight:750,color:'#526173'}}>{METODY.find(m=>m.kod===form.metoda_kod)?.wzor}</Typography>
          </Box>}
        </Paper>

        <Paper sx={{...card,p:2,display:'grid',gap:1.5,height:'100%',gridColumn:{xs:'auto',md:'1 / -1',xl:'auto'}}}>
          <TextField label="Wartość początkowa" type="number" value={form.wartosc_poczatkowa} onChange={e=>setForm(f=>({...f,wartosc_poczatkowa:e.target.value}))}/>
          {form.metoda_kod!=='JEDNORAZOWA'&&<TextField label="Stawka roczna (%)" type="number" value={form.stawka_zastosowana_proc} onChange={e=>setForm(f=>({...f,stawka_zastosowana_proc:e.target.value}))} helperText={tryb==='SRODEK'?`Sugestia dla KŚT ${wybrany?.symbol_kst||491}: ${stawkaSug}%`:''}/>} 
          {form.metoda_kod==='DEGRESYWNA'&&<TextField label="Współczynnik degresywny" type="number" inputProps={{step:.1,min:1,max:2}} value={form.wspolczynnik_degresywny} onChange={e=>setForm(f=>({...f,wspolczynnik_degresywny:e.target.value}))} helperText="W tej wersji maksymalnie 2,00."/>}
          {tryb==='SRODEK'&&<TextField
            label="Data zakupu środka"
            type="date"
            InputLabelProps={{shrink:true}}
            value={dataZakupuWybranego}
            disabled
            fullWidth
            helperText={dataZakupuWybranego
              ? 'Data pobrana automatycznie z ewidencji środka trwałego.'
              : 'Dla tego środka nie zapisano daty zakupu.'}
            sx={{
              '& .MuiInputBase-root.Mui-disabled':{backgroundColor:'#f3f4f6'},
              '& .MuiInputBase-input.Mui-disabled':{WebkitTextFillColor:'#6b7280',fontWeight:600}
            }}
          />}
          <TextField
            label={tryb==='SRODEK' ? "Data pierwszego odpisu amortyzacyjnego" : "Data bazowa do wyliczenia amortyzacji"}
            type="date"
            InputLabelProps={{shrink:true}}
            inputProps={{min:'1900-01-01',max:'2099-12-31'}}
            value={form.data_rozpoczecia_ui}
            onChange={e=>{
              const start=e.target.value;
              setForm(f=>({
                ...f,
                data_rozpoczecia_ui:start,
                // Dla środka z ewidencji pole określa pierwszy miesiąc odpisu,
                // dlatego zachowujemy dotychczasowe cofnięcie o miesiąc.
                // W trybie własnej symulacji podana data jest datą bazową,
                // a backend rozpoczyna amortyzację od następnego miesiąca.
                data_wprowadzenia:start
                  ? (tryb==='SRODEK' ? poprzedniMiesiacDlaStartu(start) : start)
                  : ''
              }));
            }}
            helperText={
              tryb==='SRODEK'&&sugerowanyPierwszyOdpis
                ? `${dataZakupuWybranego ? `Na podstawie daty zakupu ${dataPL(dataZakupuWybranego)}` : 'Na podstawie dat zapisanych dla środka'} sugerowany początek amortyzacji to ${dataPL(sugerowanyPierwszyOdpis)}. To tylko sugestia — możesz wpisać także wcześniejszą datę historyczną.`
                : 'Wskaż datę bazową. Pierwszy odpis zostanie wyznaczony na pierwszy dzień następnego miesiąca.'
            }
          />
          {czyDataHistoryczna(form.data_rozpoczecia_ui)&&<Alert severity="info">
            Data historyczna jest zgodna z datami środka. System odtworzy harmonogram od wskazanego okresu i wyliczy wartość środka na dziś. Zapis oraz późniejsze zmiany pozostaną widoczne w logach.
          </Alert>}
          <TextField label="Uwagi" multiline minRows={2} value={form.uwagi} onChange={e=>setForm(f=>({...f,uwagi:e.target.value}))}/>
          {form.metoda_kod==='JEDNORAZOWA'&&<Alert severity="warning">Kalkulator policzy odpis jednorazowy, ale możliwość zastosowania tej metody należy zweryfikować pod kątem warunków podatkowych.</Alert>}
          <Button variant="contained" startIcon={<CalculateOutlined/>} onClick={policz}>Przelicz amortyzację</Button>
        </Paper>
      </Box>

      <Box sx={{display:'grid',gap:2,minWidth:0}}>
        {!wynik?<Paper sx={{...card,p:{xs:3,sm:4},textAlign:'center',minHeight:180,display:'grid',placeItems:'center'}}><Box><CalculateOutlined sx={{fontSize:42,color:'#9fb2ca'}}/><Typography fontWeight={900} sx={{mt:1}}>Wprowadź dane i kliknij „Przelicz amortyzację”</Typography><Typography color="text.secondary">Tutaj pojawi się podsumowanie, wykres wartości i harmonogram miesiąc po miesiącu.</Typography></Box></Paper>:<>
          <Paper sx={{...card,p:2}}>
            <Typography variant="h6" fontWeight={900} sx={{mb:1.5}}>Podsumowanie</Typography>
            <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',sm:'repeat(2,minmax(0,1fr))',xl:'repeat(4,minmax(0,1fr))'},gap:1.5}}>{[
              ['Metoda',metodaLabel(form.metoda_kod)],['Wartość',money(form.wartosc_poczatkowa)],['Start',String(wynik.data_rozpoczecia||'—').slice(0,10)],['Koniec',String(wynik.data_zakonczenia||'—').slice(0,10)],
              ['Stawka',form.metoda_kod==='JEDNORAZOWA'?'—':`${form.stawka_zastosowana_proc}%`],['Liczba miesięcy',wynik.liczba_miesiecy||harmonogram.length],['Suma odpisów',money(wynik.suma_odpisow||harmonogram.reduce((a,x)=>a+Number(x.kwota_odpisu||0),0))],['Wartość końcowa',money(harmonogram[harmonogram.length-1]?.wartosc_netto||0)]
            ].map(([l,v])=><Box key={l} sx={{p:1.4,bgcolor:'#f8fafc',borderRadius:2}}><Typography variant="caption" color="text.secondary">{l}</Typography><Typography fontWeight={900}>{v}</Typography></Box>)}</Box>
            {tryb==='SRODEK'&&Number(form.stawka_zastosowana_proc)!==Number(stawkaSug)&&form.metoda_kod!=='JEDNORAZOWA'&&<Alert severity="warning" sx={{mt:2}}>Zastosowana stawka {form.stawka_zastosowana_proc}% różni się od sugerowanej stawki {stawkaSug}% dla KŚT {wybrany?.symbol_kst||491}.</Alert>}
          </Paper>

          <Paper sx={{...card,p:{xs:1.2,sm:2},minWidth:0}}><Typography fontWeight={900} sx={{mb:1.5}}>Harmonogram miesiąc po miesiącu</Typography>
            <Box sx={{display:{xs:'none',md:'block'}}}>
              <Box sx={{display:'grid',gridTemplateColumns:'55px 90px repeat(3,minmax(95px,1fr)) 105px',gap:.8,p:1,bgcolor:'#f8fafc',fontWeight:850,fontSize:12}}><span>Lp.</span><span>Miesiąc</span><span>Odpis</span><span>Umorzenie</span><span>Wartość netto</span><span>Tryb</span></Box>
              {hDane.map(h=><Box key={h.lp} sx={{display:'grid',gridTemplateColumns:'55px 90px repeat(3,minmax(95px,1fr)) 105px',gap:.8,p:1,borderBottom:'1px solid #edf1f6',alignItems:'center'}}><Typography variant="body2">{h.lp}</Typography><Typography variant="body2">{String(h.miesiac).padStart(2,'0')}.{h.rok}</Typography><Typography variant="body2">{money(h.kwota_odpisu)}</Typography><Typography variant="body2">{money(h.umorzenie_narastajaco)}</Typography><Typography variant="body2" fontWeight={800}>{money(h.wartosc_netto)}</Typography><Chip size="small" label={metodaLabel(h.metoda_etapu)} sx={{justifySelf:'start'}}/></Box>)}
            </Box>
            <Box sx={{display:{xs:'grid',md:'none'},gap:1}}>{hDane.map(h=><Paper key={h.lp} variant="outlined" sx={{p:1.3,borderRadius:2,borderColor:'#e3eaf3'}}><Box sx={{display:'flex',justifyContent:'space-between',gap:1,alignItems:'center'}}><Typography fontWeight={900}>{String(h.miesiac).padStart(2,'0')}.{h.rok}</Typography><Chip size="small" label={metodaLabel(h.metoda_etapu)}/></Box><Box sx={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:1,mt:1}}>{[['Odpis',money(h.kwota_odpisu)],['Umorzenie',money(h.umorzenie_narastajaco)],['Wartość netto',money(h.wartosc_netto)],['Pozycja',h.lp]].map(([l,v])=><Box key={l} sx={{p:.9,bgcolor:'#f8fafc',borderRadius:1.3}}><Typography variant="caption" color="text.secondary">{l}</Typography><Typography variant="body2" fontWeight={850}>{v}</Typography></Box>)}</Box></Paper>)}</Box>
            {harmonogram.length>H_NA_STRONIE&&<Box sx={{display:'flex',justifyContent:'flex-end',pt:2}}><Pagination size="small" count={hPages} page={hPage} onChange={(_,p)=>setStronaH(p)} color="primary"/></Box>}
          </Paper>

          {tryb==='SRODEK'&&maUprawnienie('DEPRECIATION_MANAGE')&&<Paper sx={{...card,p:2,display:'flex',justifyContent:'space-between',alignItems:'center',gap:2,flexWrap:'wrap'}}><Box><Typography fontWeight={900}>Zapis do ewidencji</Typography><Typography variant="body2" color="text.secondary">Zapisanie zastąpi dotychczasowy plan amortyzacji tego środka.</Typography></Box><Button variant="contained" onClick={()=>setPotwierdz(true)}>Zatwierdź i zapisz amortyzację</Button></Paper>}
        </>}
      </Box>
    </Box>}

    <Dialog open={potwierdz} onClose={()=>setPotwierdz(false)} fullWidth maxWidth="sm">
      <DialogTitle>Potwierdź amortyzację</DialogTitle>
      <DialogContent sx={{pt:'10px !important'}}><Alert severity="warning" sx={{mb:2}}>Sprawdź dane przed zapisaniem. Dotychczasowy harmonogram tego środka zostanie zastąpiony nowym.</Alert><Paper variant="outlined" sx={{p:2,borderRadius:2}}>
        {[['Środek',wybrany?`${wybrany.numer_unikatowy||wybrany.numer_inw} · ${wybrany.nazwa_indeksu||wybrany.kategoria} ${wybrany.model||''}`:'—'],['Metoda',metodaLabel(form.metoda_kod)],['Wartość początkowa',money(form.wartosc_poczatkowa)],['Stawka',form.metoda_kod==='JEDNORAZOWA'?'—':`${form.stawka_zastosowana_proc}%`],['Start',wynik?.data_rozpoczecia||'—']].map(([l,v])=><Box key={l} sx={{display:'grid',gridTemplateColumns:{xs:'1fr',sm:'160px 1fr'},gap:1,py:.6,borderBottom:'1px solid #edf1f6'}}><Typography variant="body2" color="text.secondary">{l}</Typography><Typography variant="body2" fontWeight={800}>{v}</Typography></Box>)}
      </Paper></DialogContent>
      <DialogActions><Button onClick={()=>setPotwierdz(false)}>Wróć do edycji</Button><Button variant="contained" onClick={zapisz}>Tak, zapisz</Button></DialogActions>
    </Dialog>
  </Box>;
}
